import { useState, useEffect } from "react";
import {
    collection, onSnapshot, addDoc, updateDoc, setDoc,
    deleteDoc, doc, query, where, orderBy, writeBatch, limit, documentId
} from "firebase/firestore";
import { db, appId } from "../firebase";

const MAX_VISIT_LOG_DOCS = 3000;
const MAX_ATTENDANCE_DOCS = 1095; // about 3 years of daily records
const VISIT_ATTENDANCE_ACTIVE_MENUS = new Set([
    "dashboard",
    "students",
    "teachers",
    "attendance",
    "counseling",
    "statistics",
    "ai-assistant",
]);
const MEETING_MINUTES_ACTIVE_MENUS = new Set([
    "dashboard",
    "teachers",
]);
const SCHEDULE_FINANCE_ACTIVE_MENUS = new Set([
    "dashboard",
    "schedule",
    "finance",
    "statistics",
    "ai-assistant",
]);

// Guest Mode Helpers
const loadLocal = (key) => {
    try {
        const data = localStorage.getItem(key);

        // [NEW] Patch Existing Data for Product Tour
        // [NEW] Patch Existing Data for Product Tour
        let parsed = null;
        if (data) Object.assign([], JSON.parse(data)); // Safe parse
        try { parsed = JSON.parse(data); } catch { }

        // If data exists (even if empty array), return it. Only seed if null.
        if (data !== null) {
            return parsed || [];
        }




        // [NEW] Default Seed Data for Tour (Matches seedDemoData.js)
        if (key === 'guest_students') {
            const defaultData = [
                { id: "demo_s1", name: "오타니 쇼헤이", gender: "남", grade: "청년부", class: "LA다저스", phone: "010-1717-1717", birth: "1994.07.05", school: "하나마키히가시고등학교", address: "캘리포니아주 로스앤젤레스", faithState: "기존신자", notes: "성실함, 야구부 에이스, 투타겸업", userId: 'guest', createdAt: new Date().toISOString() },
                { id: "demo_s2", name: "손흥민", gender: "남", grade: "청년부", class: "토트넘", phone: "010-0707-0707", birth: "1992.07.08", school: "동북고등학교 (중퇴)", address: "영국 런던", faithState: "기존신자", notes: "축구부 주장, 찰칵 세레머니 장인", userId: 'guest', createdAt: new Date().toISOString() },
                { id: "demo_s3", name: "장원영", gender: "여", grade: "고등부", class: "아이브", phone: "010-1004-1004", birth: "2004.08.31", school: "서울공연예술고등학교", address: "서울특별시 용산구", faithState: "초신자", notes: "찬양팀 보컬 지망, 긍정적 사고방식 (럭키비키)", userId: 'guest', createdAt: new Date().toISOString() },
                { id: "demo_s4", name: "김민재", gender: "남", grade: "청년부", class: "뮌헨", phone: "010-8888-9999", birth: "1996.11.15", school: "수원공업고등학교", address: "독일 뮌헨", faithState: "기존신자", notes: "차량 봉사 지원, 든든한 수비수", userId: 'guest', createdAt: new Date().toISOString() },
                { id: "demo_s5", name: "유재석", gender: "남", grade: "장년부", class: "방송팀", phone: "010-1234-5678", birth: "1972.08.14", school: "서울예술대학", address: "서울특별시 강남구", faithState: "기존신자", notes: "레크리에이션 진행 전문가, 성실함의 아이콘", userId: 'guest', createdAt: new Date().toISOString() }
            ];
            // Persist immediately so subsequent reads (e.g. addItem) see it
            localStorage.setItem(key, JSON.stringify(defaultData));
            return defaultData;
        }
        if (key === 'guest_teachers') {
            const defaultTeacher = [
                { id: "demo_t1", name: "차범근", role: "부장", phone: "010-0909-0909", birth: "1953.05.22", address: "경기도 화성시", userId: 'guest', createdAt: new Date().toISOString() }
            ];
            localStorage.setItem(key, JSON.stringify(defaultTeacher));
            return defaultTeacher;
        }

        return [];
    } catch { return []; }
};
const saveLocal = (key, data) => localStorage.setItem(key, JSON.stringify(data));

// [MOD] Accept userProfile for organizationId-based queries
export default function useData(user, userProfile = null, loading = false, activeMenu = "dashboard") {
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [finances, setFinances] = useState([]);
    const [annualBudget, setAnnualBudget] = useState({});
    const [visitLogs, setVisitLogs] = useState([]);
    const [meetingMinutes, setMeetingMinutes] = useState([]);
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [chatHistory, setChatHistory] = useState([]);
    const organizationId = userProfile?.organizationId;

    const shouldSubscribeVisitLogs = VISIT_ATTENDANCE_ACTIVE_MENUS.has(activeMenu);
    const shouldSubscribeAttendance = VISIT_ATTENDANCE_ACTIVE_MENUS.has(activeMenu);
    const shouldSubscribeMeetingMinutes = MEETING_MINUTES_ACTIVE_MENUS.has(activeMenu);
    const shouldSubscribeSchedules = SCHEDULE_FINANCE_ACTIVE_MENUS.has(activeMenu);
    const shouldSubscribeFinances = SCHEDULE_FINANCE_ACTIVE_MENUS.has(activeMenu);

    // 1. Data Subscriptions
    useEffect(() => {
        if (loading) return; // [FIX] Wait for profile load to prevent double-fetch (User -> Org)

        if (!user) {
            setStudents([]); setTeachers([]); setSchedules([]); setFinances([]);
            setVisitLogs([]); setMeetingMinutes([]); setAttendanceLogs([]); setChatHistory([]);
            return;
        }

        // --- GUEST MODE (Local Storage) ---
        if (user.isAnonymous) {
            console.log("🔒 Guest Mode: Using Local Storage for Data");
            setStudents(loadLocal("guest_students"));
            setTeachers(loadLocal("guest_teachers"));
            setSchedules(loadLocal("guest_schedules"));
            setFinances(loadLocal("guest_finances"));
            setVisitLogs(loadLocal("guest_visitLogs"));
            setMeetingMinutes(loadLocal("guest_meetingMinutes"));

            // [FIX] Guest Mode Attendance Scanning (attendance_UID_DATE keys)
            const guestAttendance = [];
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(`attendance_${user.uid}_`)) {
                    const dateStr = key.replace(`attendance_${user.uid}_`, '');
                    try {
                        const data = JSON.parse(localStorage.getItem(key));
                        guestAttendance.push({ id: dateStr, date: dateStr, ...data });
                    } catch (e) { }
                }
            });
            setAttendanceLogs(guestAttendance);
            // [MOD] Load Budget Object (Support per-department budgets)
            const savedBudgets = localStorage.getItem("guest_budgets");
            if (savedBudgets) {
                try { setAnnualBudget(JSON.parse(savedBudgets)); } catch { setAnnualBudget({}); }
            } else {
                // Fallback for legacy data
                const legacyBudget = parseInt(localStorage.getItem("guest_budget") || "0");
                setAnnualBudget({ 'All': legacyBudget });
            }

            // Listen for storage events to sync across tabs (optional, but good)
            const handleStorage = () => {
                setStudents(loadLocal("guest_students"));
                // ... sync others if needed
            };
            window.addEventListener('storage', handleStorage);
            return () => window.removeEventListener('storage', handleStorage);
        }

        // --- AUTH MODE (Firestore) ---

        // Query builder: uses organizationId if available, otherwise userId
        const getQuery = (colName, sortField, sortOrder, limitCount) => {
            let constraints;
            if (organizationId) {
                // Organization mode: query by organizationId for shared data
                constraints = [where("organizationId", "==", organizationId)];
            } else {
                // Personal mode: query by userId (backward compatible)
                constraints = [where("userId", "==", user.uid)];
            }
            if (sortField) constraints.push(orderBy(sortField, sortOrder));
            if (typeof limitCount === "number" && limitCount > 0) constraints.push(limit(limitCount));
            return query(collection(db, colName), ...constraints);
        };

        const unsubStudents = onSnapshot(getQuery("students"),
            (snapshot) => {
                let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // [NEW] Filter by assignedDepartments for non-admin users
                const memberRole = userProfile?.memberRole;
                const assignedDepts = userProfile?.assignedDepartments;

                if (memberRole === 'teacher' && assignedDepts && assignedDepts.length > 0) {
                    // Teachers only see students in their assigned departments
                    data = data.filter(student => assignedDepts.includes(student.grade));
                }
                // Admin sees all data (no filtering)

                setStudents(data);
            });

        const unsubTeachers = onSnapshot(getQuery("teachers"),
            (snapshot) => setTeachers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

        // [NEW] Organization Settings - Shared Budget Listener
        let unsubOrgSettings = () => { };
        if (organizationId) {
            unsubOrgSettings = onSnapshot(doc(db, "organizations", organizationId), (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    // If organization has shared budgets, it takes precedence or merges
                    if (data.budgets) {
                        setAnnualBudget(data.budgets);
                    }
                }
            });
        }

        const unsubSettings = onSnapshot(doc(db, "settings", user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();

                // [FIX] Organization에 속한 경우, Personal Settings의 예산은 무시
                // Organization 예산이 우선 적용되어야 함 (관리자가 설정한 예산을 모두가 봐야 함)
                if (!organizationId) {
                    // Only load personal budgets if NOT in an organization
                    if (data.budgets && typeof data.budgets === 'object') {
                        setAnnualBudget(data.budgets);
                    } else {
                        // Fallback: If amount exists, map it to 'All'. If neither, empty object.
                        const legacyAmount = data.amount || 0;
                        setAnnualBudget({ 'All': legacyAmount });
                    }
                }
                // Note: If organizationId exists, budget is loaded from Organization Settings (line 199-210)

                // [FIX] Load myDepartments from settings
                if (data.myDepartments && Array.isArray(data.myDepartments)) {
                    setMyDepartments(data.myDepartments);
                }
            } else {
                // If personal settings don't exist, we might still have Org settings, so don't reset to 0 blindly?
                // setAnnualBudget({ 'All': 0 }); 
            }
        });

        const savedChat = localStorage.getItem(`chatHistory_${user.uid}`);
        if (savedChat) {
            try { setChatHistory(JSON.parse(savedChat)); } catch (e) { }
        }

        return () => {
            unsubStudents(); unsubTeachers();
            unsubSettings();
            unsubOrgSettings(); // Unsubscribe Org Listener
        };
    }, [user?.uid, userProfile?.organizationId, loading]); // [MOD] Re-run queries when organization changes

    // 2. Schedule/Finance Subscriptions (menu-aware)
    useEffect(() => {
        if (loading || !user || user.isAnonymous) return;
        if (!shouldSubscribeSchedules && !shouldSubscribeFinances) return;

        const getScopedQuery = (colName) => {
            const constraints = [];
            if (organizationId) constraints.push(where("organizationId", "==", organizationId));
            else constraints.push(where("userId", "==", user.uid));
            return query(collection(db, colName), ...constraints);
        };

        let unsubSchedules = null;
        let unsubFinances = null;

        if (shouldSubscribeSchedules) {
            unsubSchedules = onSnapshot(getScopedQuery("schedules"),
                (snapshot) => {
                    const data = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
                    data.sort((a, b) => a.date > b.date ? 1 : -1);
                    setSchedules(data);
                });
        }

        if (shouldSubscribeFinances) {
            unsubFinances = onSnapshot(getScopedQuery("finances"),
                (snapshot) => {
                    const data = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
                    data.sort((a, b) => a.date < b.date ? 1 : -1);
                    setFinances(data);
                });
        }

        return () => {
            if (unsubSchedules) unsubSchedules();
            if (unsubFinances) unsubFinances();
        };
    }, [user?.uid, organizationId, loading, shouldSubscribeSchedules, shouldSubscribeFinances]);

    // 3. Meeting Minutes Subscription (menu-aware)
    useEffect(() => {
        if (loading || !user || user.isAnonymous || !shouldSubscribeMeetingMinutes) return;

        const getScopedQuery = () => {
            const constraints = [];
            if (organizationId) constraints.push(where("organizationId", "==", organizationId));
            else constraints.push(where("userId", "==", user.uid));
            return query(collection(db, "meetingMinutes"), ...constraints);
        };

        const unsubMinutes = onSnapshot(getScopedQuery(),
            (snapshot) => {
                const data = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
                data.sort((a, b) => a.date < b.date ? 1 : -1);
                setMeetingMinutes(data);
            });

        return () => {
            unsubMinutes();
        };
    }, [user?.uid, organizationId, loading, shouldSubscribeMeetingMinutes]);

    // 4. Heavy Subscriptions (menu-aware)
    useEffect(() => {
        if (loading || !user || user.isAnonymous) return;
        if (!shouldSubscribeVisitLogs && !shouldSubscribeAttendance) return;

        const getScopedQuery = (colName, sortField, sortOrder, limitCount) => {
            const constraints = [];
            if (organizationId) constraints.push(where("organizationId", "==", organizationId));
            else constraints.push(where("userId", "==", user.uid));
            if (sortField) constraints.push(orderBy(sortField, sortOrder));
            if (typeof limitCount === "number" && limitCount > 0) constraints.push(limit(limitCount));
            return query(collection(db, colName), ...constraints);
        };

        let unsubVisitLogs = null;
        let unsubVisitLogsFallback = null;
        if (shouldSubscribeVisitLogs) {
            const applyVisitLogsSnapshot = (snapshot) => {
                const data = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
                data.sort((a, b) => a.date < b.date ? 1 : -1);
                setVisitLogs(data);
            };

            unsubVisitLogs = onSnapshot(
                getScopedQuery("visitLogs", "date", "desc", MAX_VISIT_LOG_DOCS),
                applyVisitLogsSnapshot,
                (error) => {
                    const isIndexIssue = error?.code === "failed-precondition" || error?.message?.includes("index");
                    if (!isIndexIssue) {
                        console.error("visitLogs listener error:", error);
                        return;
                    }
                    if (process.env.NODE_ENV !== "production") {
                        console.info("[useData] visitLogs 제한 쿼리 인덱스 미구성 → 기본 쿼리로 폴백");
                    }
                    if (!unsubVisitLogsFallback) {
                        unsubVisitLogsFallback = onSnapshot(getScopedQuery("visitLogs"), applyVisitLogsSnapshot);
                    }
                }
            );
        }

        let unsubAttendance = null;
        let unsubAttendanceFallback = null;
        if (shouldSubscribeAttendance) {
            const attendanceRef = organizationId
                ? collection(db, "organizations", organizationId, "attendance")
                : collection(db, "artifacts", appId, "users", user.uid, "attendance");

            const applyAttendanceSnapshot = (snapshot) => {
                setAttendanceLogs(snapshot.docs.map((docItem) => ({
                    id: docItem.id,
                    date: docItem.id,
                    ...docItem.data()
                })));
            };

            unsubAttendance = onSnapshot(
                query(attendanceRef, orderBy(documentId(), "desc"), limit(MAX_ATTENDANCE_DOCS)),
                applyAttendanceSnapshot,
                (error) => {
                    console.error("attendance limited listener error, fallback to full listener:", error);
                    if (!unsubAttendanceFallback) {
                        unsubAttendanceFallback = onSnapshot(attendanceRef, applyAttendanceSnapshot);
                    }
                }
            );
        }

        return () => {
            if (unsubVisitLogs) unsubVisitLogs();
            if (unsubVisitLogsFallback) unsubVisitLogsFallback();
            if (unsubAttendance) unsubAttendance();
            if (unsubAttendanceFallback) unsubAttendanceFallback();
        };
    }, [
        user?.uid,
        organizationId,
        loading,
        shouldSubscribeVisitLogs,
        shouldSubscribeAttendance,
    ]);

    // 5. CRUD Helpers (Polymorphic)
    const addItem = async (collectionName, data) => {
        if (!user) return;

        // GUEST MODE
        if (user.isAnonymous) {
            const newItem = { id: Date.now().toString(), ...data, userId: 'guest', createdAt: new Date().toISOString() };
            const key = `guest_${collectionName}`;
            const current = loadLocal(key);
            const updated = [...current, newItem];
            saveLocal(key, updated);

            // Manually update state to reflect change locally immediately
            if (collectionName === 'students') setStudents(updated);
            if (collectionName === 'teachers') setTeachers(updated);
            if (collectionName === 'schedules') setSchedules(updated);
            if (collectionName === 'finances') setFinances(updated);
            if (collectionName === 'visitLogs') setVisitLogs(updated);
            if (collectionName === 'meetingMinutes') setMeetingMinutes(updated);
            if (collectionName === 'attendanceLogs') setAttendanceLogs(updated); // Careful with naming mismatch in Seed?

            return true; // [FIX] 게스트 모드에서도 성공 반환
        }

        // AUTH MODE
        try {
            // [MOD] Include organizationId for data sharing
            const organizationId = userProfile?.organizationId;
            const docData = {
                ...data,
                userId: user.uid,
                createdBy: user.uid, // Track who created the document
                createdAt: new Date().toISOString()
            };

            // Add organizationId if exists (for shared data)
            if (organizationId) {
                docData.organizationId = organizationId;
            }

            // [FIX] If data.id is provided, use setDoc to save with that specific ID
            // This ensures update/delete can find the document later
            if (data.id) {
                const docId = String(data.id);
                await setDoc(doc(db, collectionName, docId), docData);
            } else {
                await addDoc(collection(db, collectionName), docData);
            }
            return true; // Success
        } catch (error) {
            console.error(`Error adding to ${collectionName}:`, error);
            // alert("저장 실패"); // App.js에서 처리할 수 있도록 제거하거나 유지
            return false; // Failed
        }
    };

    const bulkAddItem = async (collectionName, items) => {
        if (!user) return;

        // GUEST MODE
        if (user.isAnonymous) {
            const newItems = items.map((item, idx) => ({
                id: (Date.now() + idx).toString(),
                ...item,
                userId: 'guest',
                createdAt: new Date().toISOString()
            }));
            const key = `guest_${collectionName}`;
            const current = loadLocal(key);
            const updated = [...current, ...newItems];
            saveLocal(key, updated);

            if (collectionName === 'students') setStudents(updated);
            if (collectionName === 'teachers') setTeachers(updated);
            if (collectionName === 'schedules') setSchedules(updated);
            if (collectionName === 'finances') setFinances(updated);
            if (collectionName === 'visitLogs') setVisitLogs(updated);
            if (collectionName === 'meetingMinutes') setMeetingMinutes(updated);

            return true;
        }

        // AUTH MODE
        try {
            const batch = writeBatch(db);
            const organizationId = userProfile?.organizationId;

            items.forEach((item) => {
                const docRef = item.id ? doc(db, collectionName, String(item.id)) : doc(collection(db, collectionName));
                const docData = {
                    ...item,
                    userId: user.uid,
                    createdBy: user.uid,
                    createdAt: new Date().toISOString()
                };
                if (organizationId) {
                    docData.organizationId = organizationId;
                }
                batch.set(docRef, docData);
            });

            await batch.commit();
            return true;
        } catch (error) {
            console.error(`Error bulk adding to ${collectionName}:`, error);
            return false;
        }
    };

    const updateItem = async (collectionName, id, data) => {
        if (!user) return;

        // GUEST MODE
        if (user.isAnonymous) {
            const key = `guest_${collectionName}`;
            const current = loadLocal(key);
            const updated = current.map(item => item.id === id ? { ...item, ...data } : item);
            saveLocal(key, updated);

            if (collectionName === 'students') setStudents(updated);
            if (collectionName === 'teachers') setTeachers(updated);
            if (collectionName === 'schedules') setSchedules(updated);
            if (collectionName === 'finances') setFinances(updated);
            if (collectionName === 'visitLogs') setVisitLogs(updated);
            if (collectionName === 'meetingMinutes') setMeetingMinutes(updated);
            if (collectionName === 'attendanceLogs') setAttendanceLogs(updated);
            return true; // [FIX] 게스트 모드에서도 성공 반환
        }

        // AUTH MODE
        try {
            // [FIX] Ensure id is a string for Firestore
            const docId = String(id);
            const docRef = doc(db, collectionName, docId);
            // Firestore rejects undefined field values; strip them before updating.
            const sanitized = Object.fromEntries(
                Object.entries(data || {}).filter(([, v]) => v !== undefined)
            );
            await updateDoc(docRef, sanitized);
            return true; // Success
        } catch (error) {
            console.error(`Error updating ${collectionName}:`, error);
            // alert("수정 실패");
            return false; // Failed
        }
    };

    const deleteItem = async (collectionName, id) => {
        if (!user) return;

        // GUEST MODE
        if (user.isAnonymous) {
            const key = `guest_${collectionName}`;
            const current = loadLocal(key);
            const updated = current.filter(item => item.id !== id);
            saveLocal(key, updated);

            // [FIX] Update State Immediately
            if (collectionName === 'students') setStudents(updated);
            if (collectionName === 'teachers') setTeachers(updated);
            if (collectionName === 'schedules') setSchedules(updated);
            if (collectionName === 'finances') setFinances(updated);
            if (collectionName === 'visitLogs') setVisitLogs(updated);
            if (collectionName === 'meetingMinutes') setMeetingMinutes(updated);
            if (collectionName === 'attendanceLogs') setAttendanceLogs(updated);
            return true; // [FIX] 게스트 모드에서도 성공 반환
        }

        // AUTH MODE
        try {
            // [FIX] Ensure id is a string for Firestore
            const docId = String(id);
            await deleteDoc(doc(db, collectionName, docId));
            return true; // Success
        } catch (error) {
            console.error(`Error deleting from ${collectionName}:`, error);
            // alert("삭제 실패");
            return false; // Failed
        }
    };

    const handleUpdateAnnualBudget = async (amount, department = 'All') => {
        if (!user) return;

        // Create new budget object based on current state
        const updatedBudgets = { ...annualBudget, [department]: amount };

        if (user.isAnonymous) {
            localStorage.setItem("guest_budgets", JSON.stringify(updatedBudgets)); // New Key for Object
            setAnnualBudget(updatedBudgets);
            return;
        }

        try {
            // [MOD] If part of an organization, save to Organization Settings (Shared)
            const organizationId = userProfile?.organizationId;
            if (organizationId) {
                await setDoc(doc(db, "organizations", organizationId), { budgets: updatedBudgets }, { merge: true });
            } else {
                // Legacy: Save to Personal Settings
                await setDoc(doc(db, 'settings', user.uid), { budgets: updatedBudgets, userId: user.uid }, { merge: true });
            }
            // State update will happen via subscription
        } catch (error) {
            console.error("Budget update error", error);
            alert("예산 수정 실패");
        }
    };

    const [myDepartments, setMyDepartments] = useState([]);

    // 2. Fetch My Departments (Settings)
    useEffect(() => {
        if (!user) return;
        if (user.isAnonymous) {
            const saved = localStorage.getItem("guest_myDepartments");
            if (saved) setMyDepartments(JSON.parse(saved));
            return;
        }
        // Subscribed in main effect above
    }, [user]);

    const handleUpdateMyDepartments = async (depts) => {
        if (!user) return;
        if (user.isAnonymous) {
            localStorage.setItem("guest_myDepartments", JSON.stringify(depts));
            setMyDepartments(depts);
            return;
        }
        try {
            await setDoc(doc(db, 'settings', user.uid), { myDepartments: depts, userId: user.uid }, { merge: true });
            setMyDepartments(depts); // [FIX] Update local state after saving
        } catch (error) {
            console.error("Departments update error", error);
            alert("부서 설정 수정 실패");
        }
    };

    const refreshAttendanceLogs = () => {
        if (!user) return;

        // GUEST MODE REFRESH
        if (user.isAnonymous) {
            const guestAttendance = [];
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(`attendance_${user.uid}_`)) {
                    const dateStr = key.replace(`attendance_${user.uid}_`, '');
                    try {
                        const data = JSON.parse(localStorage.getItem(key));
                        guestAttendance.push({ id: dateStr, date: dateStr, ...data });
                    } catch (e) { }
                }
            });
            setAttendanceLogs(guestAttendance);
        }
        // Auth mode relies on onSnapshot, so no manual refresh needed usually.
    };

    return {
        students, teachers, schedules, finances, annualBudget,
        visitLogs, meetingMinutes, attendanceLogs, chatHistory, setChatHistory,
        addItem, bulkAddItem, updateItem, deleteItem, handleUpdateAnnualBudget,
        myDepartments, handleUpdateMyDepartments, refreshAttendanceLogs
    };
}
